/**
 * Polar Profile & Interpolation Module
 * Creates wing-loading-adjusted polar profiles and provides
 * fast binary-search interpolation of sink rates.
 *
 * Ported from xc-polar-comparison/compareService.browser.js
 */

/**
 * Get default wing loading from polar metadata
 */
export function getPolarDefaultWingLoading(polar) {
  if (Number.isFinite(polar?.defaultWingLoading) && polar.defaultWingLoading > 0) {
    return polar.defaultWingLoading;
  }
  if (
    !polar
    || !Number.isFinite(polar.mass)
    || !Number.isFinite(polar.wingArea)
    || polar.mass <= 0
    || polar.wingArea <= 0
  ) {
    return null;
  }
  return polar.mass / polar.wingArea;
}

export function clampPolarWingLoading(polar, wingLoading) {
  if (!Number.isFinite(wingLoading) || wingLoading <= 0) {
    return wingLoading;
  }

  const maxCandidates = [
    polar?.maxWingLoading,
    polar?.maxNoFixedWingLoading
  ]
    .map((value) => Number.parseFloat(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (!maxCandidates.length) {
    return wingLoading;
  }

  return Math.min(wingLoading, Math.max(...maxCandidates));
}

/**
 * Build a wing-loading-adjusted polar profile from dense polar data.
 *
 * @param {object} polar - Polar object with polarSpeeds[], polarSinks[], referenceWingLoading, etc.
 * @param {number|null} wingLoading - Target wing loading in kg/m² (null = use reference)
 * @returns {{ points: Array<{speedMs: number, sinkMs: number}>, minSpeedMs: number, maxSpeedMs: number } | null}
 */
export function getAdjustedPolarProfile(polar, wingLoading) {
  if (!polar) return null;

  const resolvedWingLoading = clampPolarWingLoading(polar, wingLoading);

  const basePoints = (polar.polarSpeeds || [])
    .map((speedKmh, idx) => ({
      speedKmh,
      sinkMs: polar.polarSinks[idx]
    }))
    .filter((point) => Number.isFinite(point.speedKmh) && point.speedKmh > 0 && Number.isFinite(point.sinkMs))
    .sort((a, b) => a.speedKmh - b.speedKmh);

  if (basePoints.length < 2) return null;

  // Deduplicate
  const deduped = [];
  basePoints.forEach((point) => {
    const existing = deduped.find((entry) => Math.abs(entry.speedKmh - point.speedKmh) < 1e-6);
    if (existing) {
      existing.sinkMs = point.sinkMs;
      return;
    }
    deduped.push({ ...point });
  });
  if (deduped.length < 2) return null;

  // Compute wing loading scale factor
  const refLoading = Number.isFinite(polar.referenceWingLoading) && polar.referenceWingLoading > 0
    ? polar.referenceWingLoading
    : getPolarDefaultWingLoading(polar);
  let scaleFactor = 1;
  if (
    Number.isFinite(resolvedWingLoading)
    && resolvedWingLoading > 0
    && Number.isFinite(refLoading)
    && refLoading > 0
    && Math.abs(resolvedWingLoading - refLoading) > 1e-6
  ) {
    scaleFactor = Math.sqrt(resolvedWingLoading / refLoading);
  }

  // Scale speeds and sinks
  const points = deduped
    .map((point) => ({
      speedMs: (point.speedKmh / 3.6) * scaleFactor,
      sinkMs: point.sinkMs * scaleFactor
    }))
    .sort((a, b) => a.speedMs - b.speedMs);

  return {
    points,
    minSpeedMs: points[0].speedMs,
    maxSpeedMs: points[points.length - 1].speedMs
  };
}

/**
 * Interpolate sink rate at a given speed using binary search + linear interpolation.
 *
 * @param {object} profile - From getAdjustedPolarProfile()
 * @param {number} speedMs - Speed in m/s
 * @returns {number|null} Sink rate in m/s (negative value)
 */
export function interpolateSinkAtSpeed(profile, speedMs) {
  if (!profile || !Array.isArray(profile.points) || profile.points.length === 0) return null;
  const points = profile.points;
  const first = points[0];
  const last = points[points.length - 1];
  if (speedMs <= first.speedMs) return first.sinkMs;
  if (speedMs >= last.speedMs) return last.sinkMs;

  let low = 0;
  let high = points.length - 1;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (speedMs <= points[mid].speedMs) high = mid;
    else low = mid;
  }

  const prev = points[low];
  const next = points[high];
  const span = next.speedMs - prev.speedMs;
  if (span <= 1e-9) return next.sinkMs;
  const ratio = (speedMs - prev.speedMs) / span;
  return prev.sinkMs + ratio * (next.sinkMs - prev.sinkMs);
}
